import Phaser from 'phaser';

import LetterPartyBackgroundRenderer from '@/features/letterParty/game/rendering/background';
import LetterPartyEntityRenderer from '@/features/letterParty/game/rendering/entities';
import LottieCanvas from '@/features/letterParty/game/rendering/lottieCanvas';

import type {
	ConfettiCollectible,
	Direction,
	HatCollectible,
	LetterPartyTheme,
	LetterQ,
	Position,
} from '@/features/letterParty/types';

export interface LetterPartyBoardState {
	gridWidth: number;
	gridHeight: number;
	cellSize: number;
	playAreaTop: number;
	playAreaBottom: number;
	pixelRatio: number;
	showMovementButtons: boolean;
	snake: Position[];
	currentDirection: Direction;
	hatCount: number;
	hats: HatCollectible[];
	confettis: ConfettiCollectible[];
	letterQs: LetterQ[];
	isRecovering: boolean;
	waitingForFirstDirection: boolean;
	isStopped: boolean;
}

export default class Board {
	private backgroundRenderer: LetterPartyBackgroundRenderer;
	private entityRenderer: LetterPartyEntityRenderer;
	private staticDependencies: readonly unknown[] = [];
	private staticLottieKeys = new Set<string>();
	private staticObjects: Phaser.GameObjects.GameObject[] = [];

	constructor(
		private readonly scene: Phaser.Scene,
		private readonly lottieRenderer: LottieCanvas,
		theme: LetterPartyTheme,
	) {
		this.backgroundRenderer = new LetterPartyBackgroundRenderer(scene, lottieRenderer, this.addStaticObject, theme);
		this.entityRenderer = new LetterPartyEntityRenderer(scene, lottieRenderer, this.addStaticObject);
	}

	public renderBoard(state: LetterPartyBoardState) {
		const {
			gridWidth,
			gridHeight,
			hats,
			confettis,
			letterQs,
			snake,
			currentDirection,
			hatCount,
			isRecovering,
			waitingForFirstDirection,
			showMovementButtons,
		} = state;
		const { pixelRatio } = state;
		const isCollisionPause = isRecovering && !waitingForFirstDirection;
		const cellSize = state.cellSize * pixelRatio;
		const playAreaTop = state.playAreaTop * pixelRatio;
		const playAreaBottom = state.playAreaBottom * pixelRatio;
		const stageWidth = this.scene.scale.width;
		const stageHeight = this.scene.scale.height;
		const boardWidth = gridWidth * cellSize;
		const boardHeight = gridHeight * cellSize;
		const playAreaHeight = Math.max(boardHeight, stageHeight - playAreaTop - playAreaBottom);
		const originX = (stageWidth - boardWidth) / 2;
		const originY = playAreaTop + (playAreaHeight - boardHeight) / 2;
		const nextStaticDependencies = [
			gridWidth,
			gridHeight,
			cellSize,
			pixelRatio,
			stageWidth,
			stageHeight,
			originX,
			originY,
			snake.length,
			showMovementButtons,
			hats,
			confettis,
			letterQs,
			isRecovering,
		];

		this.lottieRenderer.beginFrame();

		if (this.haveStaticDependenciesChanged(nextStaticDependencies)) {
			this.clearStaticObjects();
			this.backgroundRenderer.render(stageWidth, stageHeight, snake.length, pixelRatio, showMovementButtons);
			this.entityRenderer.drawGrid(originX, originY, gridWidth, gridHeight, cellSize, pixelRatio);

			hats.forEach((hat) => {
				if (!hat.collected) {
					this.entityRenderer.drawHat(hat.position, originX, originY, cellSize);
				}
			});

			confettis.forEach((confetti) => {
				if (!confetti.collected) {
					this.entityRenderer.drawConfetti(confetti.position, originX, originY, cellSize);
				}
			});

			letterQs.forEach((letterQ) => {
				if (!letterQ.collected) {
					this.entityRenderer.drawLetterCharacter(letterQ, originX, originY, cellSize);
				}
			});

			if (isRecovering) {
				this.addStaticObject(
					this.scene.add
						.rectangle(originX, originY, gridWidth * cellSize, gridHeight * cellSize, 0xff6b0f, 0.16)
						.setOrigin(0)
						.setDepth(1000),
				);
			}

			this.staticDependencies = nextStaticDependencies;
			this.staticLottieKeys = this.lottieRenderer.getActiveKeys();
		} else {
			this.lottieRenderer.retain(this.staticLottieKeys);
		}

		this.entityRenderer.drawSnake({
			snake,
			currentDirection,
			hatCount,
			originX,
			originY,
			cellSize,
			isMoving: !state.waitingForFirstDirection && !state.isStopped && !state.isRecovering,
			isCollisionPause,
		});

		this.lottieRenderer.removeInactive();
	}

	public clear() {
		this.clearStaticObjects();
		this.staticDependencies = [];
		this.staticLottieKeys.clear();
	}

	private haveStaticDependenciesChanged(nextDependencies: readonly unknown[]) {
		return (
			this.staticDependencies.length !== nextDependencies.length ||
			nextDependencies.some((dependency, index) => dependency !== this.staticDependencies[index])
		);
	}

	private addStaticObject = (object: Phaser.GameObjects.GameObject | Phaser.GameObjects.GameObject[]) => {
		if (Array.isArray(object)) {
			this.staticObjects.push(...object);
			return;
		}

		this.staticObjects.push(object);
	};

	private clearStaticObjects() {
		this.staticObjects.forEach((object) => object.destroy());
		this.staticObjects.length = 0;
	}
}
